import type { TConfirmationModalRef } from "#/components/exam-market/management/ConfirmationModal";
import ConfirmationModal from "#/components/exam-market/management/ConfirmationModal";
import type { TExamMarketModalRef } from "#/components/exam-market/management/ExamMarketModal";
import ExamMarketModal from "#/components/exam-market/management/ExamMarketModal";
import type { IExamMarketManagementForm } from "#/components/exam-market/management/SearchForm";
import SearchForm, {
  initialValues,
} from "#/components/exam-market/management/SearchForm";
import { api, auth } from "#/utils";
import { useLoading } from "#/utils/context/loading";
import dayjs from "#/utils/date/dayjs";
import type { TColumn } from "@monta/design-system";
import { Breadcrumb, Button, Card, Table, Toast } from "@monta/design-system";
import { Info } from "@monta/icons";
import { fixString, useSearchParams } from "@monta/router-react";
import clsx from "clsx";
import type { RefObject } from "react";
import { useEffect, useRef, useState } from "react";
import styles from "./management.module.scss";

const breadcrumbItems = [
  {
    label: "خانه",
    link: fixString("/", "LANDING_URL"),
  },
  {
    label: "مدیریت اگزم مارکت",
    link: window.location.href,
  },
];

const isAdmin = () => auth.user?.id === 1120727; // SIMA USER ID

export type TData = {
  resultCode: number;
  resultText: string;
  productName: string;
  primaryProductId: number;
  examDatetime: string;
  examRequestId: number;
  questionNo: number;
  providerName: string;
  examTakingDetailId: number;
  statusId: number;
  examNo: number;
  examRequestGrade: number;
  examRequestMajorId: number;
  examRequestName: string;
};

export enum Providers {
  واله = 1,
  مهدی_آرام_فر = 2,
  محمد_کنفچیان = 3,
  خوشخوان = 4,
  اکانت_اگزم_مارکت = 5,
  لیموترش = 6,
  مصلایی = 7,
}

export const ProvidersList = [
  { id: 1, name: "واله" },
  { id: 2, name: "مهدی آرام فر" },
  { id: 3, name: "محمد کنفچیان " },
  { id: 4, name: "خوشخوان" },
  { id: 5, name: "اکانت اگزم مارکت" },
  { id: 6, name: "لیموترش" },
  { id: 7, name: "مصلایی" },
  { id: 9, name: "علوی" },
  { id: 12, name: "تام‌لند" },
];

export const ExamMarketStatus = [
  { id: 1, en: "Waiting For ExamRequestID", fa: "در انتظار ورود کد آزمون" },
  {
    id: 2,
    en: "Waiting For Confirming ExamRequest by Provider",
    fa: "در انتظار تایید تامین کننده",
  },
  {
    id: 3,
    en: "Waiting For generate ExamTaking by Monta",
    fa: "در انتظار تایید منتا",
  },
  { id: 4, en: "Exam Request is rejected by monta", fa: "نیاز به ویرایش" },
  {
    id: 5,
    en: "taking is inserted,wating for exam time",
    fa: " در انتظار زمان اجرا",
  },
  { id: 6, en: "exam time", fa: "در حال اجرا" },
  { id: 7, en: "after exam time", fa: "برگزار شده" },
] as const;

export const ExamMarketStatusMessages = Object.fromEntries(
  ExamMarketStatus.map((i) => [i.id, i.fa]),
) as Record<
  (typeof ExamMarketStatus)[number]["id"],
  (typeof ExamMarketStatus)[number]["fa"]
>;

export type TExamMarketGenericResponse<T> = {
  resultCode: number;
  resultText: string;
} & T;

const getColumns = (
  showLoading: () => void,
  hideLoading: () => void,
  examMarketModal?: RefObject<TExamMarketModalRef>,
  confirmationModal?: RefObject<TConfirmationModalRef>,
  updateRow?: (data: TData, index: number) => void,
) =>
  (
    [
      {
        header: "ردیف",
        cellRenderer(field, row, index) {
          return index + 1 + "-";
        },
      },
      isAdmin() && {
        header: "نام تامین کننده",
        field: "providerName",
      },
      {
        header: "نام محصول",
        field: "productName",
      },
      {
        header: "کد محصول",
        field: "primaryProductId",
      },
      {
        header: "تاریخ",
        field: "examDatetime",
        cellRenderer(field, row, index) {
          return field
            ? dayjs(field).calendar("jalali").format("YYYY/MM/DD")
            : "";
        },
      },
      {
        header: "کد آزمون",
        field: "examRequestId",
        editable(row) {
          return !isAdmin() && [1, 2, 4].includes(row.statusId);
        },
        editInputProps: {
          placeholder: "کد آزمون را وارد کنید",
          style: {
            width: "135px",
          },
        },
        cellRenderer(field, row, index) {
          return (
            field || (
              <div className={styles["empty-cell"]}>
                {isAdmin() ? "بدون کد آزمون" : "کد آزمون را وارد کنید"}
              </div>
            )
          );
        },
      },
      {
        header: "تعداد سوالات",
        field: "questionNo",
        cellRenderer(field, row, index) {
          return field ? (
            isAdmin() ? (
              <div
                className={styles["underline"]}
                onClick={() => {
                  examMarketModal?.current?.open(row);
                }}
              >
                {field}
              </div>
            ) : (
              `${field}`
            )
          ) : (
            <div className={styles["empty-cell"]}>بدون کد آزمون</div>
          );
        },
      },
      isAdmin() && {
        header: "کد اجرا",
        field: "examTakingDetailId",
        cellRenderer(field, row, index) {
          return field || <div className={styles["empty-cell"]}>اجرا نشده</div>;
        },
      },
      {
        header: "وضعیت",
        field: "statusId",
        cellRenderer(field) {
          return (
            <div
              className={clsx(
                styles["status-chip"],
                styles[`status-chip--${field}`],
              )}
            >
              {ExamMarketStatusMessages[field]}
            </div>
          );
        },
      },
      {
        divider: "before",
        header: "عملیات",
        cellRenderer(field, row, index) {
          return (
            <div className={styles["actions"]}>
              {row.statusId !== 7 && (
                <Button
                  disabled={(isAdmin() ? [1, 2, 5, 6] : [1, 3, 5, 6]).includes(
                    row.statusId,
                  )}
                  className={styles["actions__action--icon"]}
                  onClick={() => {
                    window.open(
                      fixString(
                        `/#!examGenerator;a2=${row.examRequestId};a4=${row.examRequestGrade};a3=${
                          row.examRequestMajorId
                        };a5=${encodeURIComponent(row.examRequestName)};a6=a8`,
                        "GWT_URL",
                      ),
                      "_target",
                    );
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2.75C5.063 2.75 2.75 5.063 2.75 12C2.75 18.937 5.063 21.25 12 21.25C18.937 21.25 21.25 18.937 21.25 12"
                      stroke="#1D2939"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M19.5285 4.30461V4.30461C18.5355 3.42561 17.0185 3.51761 16.1395 4.51061C16.1395 4.51061 11.7705 9.44562 10.2555 11.1586C8.73853 12.8706 9.85053 15.2356 9.85053 15.2356C9.85053 15.2356 12.3545 16.0286 13.8485 14.3406C15.3435 12.6526 19.7345 7.69362 19.7345 7.69362C20.6135 6.70062 20.5205 5.18361 19.5285 4.30461Z"
                      stroke="#FF6A00"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M15.0088 5.80078L18.6038 8.98378"
                      stroke="#FF6A00"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Button>
              )}
              {/* {row.statusId === 7 && (
                <Button
                  disabled={[1, 5, 6].includes(row.statusId)}
                  className={styles['actions__action--secondary']}
                >
                  گزارش آزمون
                </Button>
              )} */}
              <Button
                disabled={[1].includes(row.statusId)}
                className={styles["actions__action--secondary"]}
                onClick={() => {
                  window.open(
                    fixString(
                      `/content-project/check-exam-topic?code=${row.examRequestId}`,
                      "LEGACY_APPLICATION_URL",
                    ),
                    "_target",
                  );
                }}
              >
                بررسی سر فصل
              </Button>
              {isAdmin() && row.statusId !== 7 && (
                <Button
                  disabled={[1, 2, 4, 5, 6, 7].includes(row.statusId)}
                  className={styles["actions__action--secondary"]}
                  onClick={() => {
                    confirmationModal?.current?.open(() => {
                      showLoading();
                      api
                        .$post<
                          TExamMarketGenericResponse<{ statusId: number }>
                        >("/exam/rejectExamRequestByMonta", null, {
                          params: { primaryProductId: row.primaryProductId },
                        })
                        .then((res) => {
                          if (res.resultCode > 0) {
                            updateRow?.(
                              { ...row, statusId: res.statusId },
                              index,
                            );
                          } else {
                            console.error(res);
                            Toast.error({
                              message: "خطایی رخ داده",
                              position: "bottom",
                            });
                          }
                        })
                        .finally(hideLoading);
                    });
                  }}
                >
                  عدم تایید منتا
                </Button>
              )}
              {row.statusId !== 7 && (
                <>
                  <Button
                    disabled={(isAdmin()
                      ? [1, 2, 4, 5, 6]
                      : [1, 3, 5, 6]
                    ).includes(row.statusId)}
                    className={styles["actions__action--primary"]}
                    onClick={() => {
                      if (isAdmin()) {
                        const callback = encodeURIComponent(
                          fixString(
                            `/exam-market/management?rowData=${btoa(
                              encodeURIComponent(JSON.stringify(row)),
                            )}`,
                            "APPLICATION_URL",
                          ),
                        );
                        window.location.href = fixString(
                          `/taking-exam-generator?a1=${
                            row.examRequestId
                          }&callback=${encodeURIComponent(callback)}`,
                          "LEGACY_APPLICATION_URL",
                        );
                      } else {
                        examMarketModal?.current?.open(row, () => {
                          showLoading();
                          api
                            .$post<
                              TExamMarketGenericResponse<{
                                statusId: number;
                              }>
                            >("/exam/confirmExamRequestByProvider", null, {
                              params: {
                                primaryProductId: row.primaryProductId,
                              },
                            })
                            .then((res) => {
                              if (res.resultCode > 0)
                                updateRow?.(
                                  { ...row, statusId: res.statusId },
                                  index,
                                );
                              else {
                                console.error(res);
                                Toast.error({
                                  message: "خطایی رخ داده",
                                  position: "bottom",
                                });
                              }
                            })
                            .finally(hideLoading);
                        });
                      }
                    }}
                  >
                    {isAdmin() ? "اجرای آزمون" : "تایید نهایی"}
                  </Button>
                </>
              )}
            </div>
          );
        },
      },
    ] as (TColumn<TData> | false)[]
  ).filter(Boolean) as TColumn<TData>[];

const ExamMarketManagement = () => {
  const [data, setData] = useState<TData[]>([]);
  const [formValues, setFormValues] =
    useState<IExamMarketManagementForm>(initialValues);
  const examMarketModal = useRef<TExamMarketModalRef>(null);
  const confirmationModal = useRef<TConfirmationModalRef>(null);
  const { hideLoading, showLoading, isLoading } = useLoading();
  const [params] = useSearchParams();
  const rowData = params.get("rowData");
  const codeTaking = params.get("codeTaking");
  const urlData =
    rowData && (JSON.parse(decodeURIComponent(atob(rowData))) as TData);

  const getData = () => {
    showLoading();
    api
      .$post<TData[]>("/exam/providerExamInfoSelect", null, {
        params: {
          ...formValues,
          fromDate: formValues.fromDate
            ? dayjs(formValues.fromDate).format("YYYY/MM/DD 00:00:00")
            : undefined,
          toDate: formValues.toDate
            ? dayjs(formValues.toDate).format("YYYY/MM/DD 00:00:00")
            : undefined,
        },
      })
      .then((res) => {
        setData(res);
      })
      .catch((e) => {
        console.error(e);
        setData([]);
        Toast.error({ message: "خطایی رخ داده", position: "bottom" });
      })
      .finally(hideLoading);
  };

  useEffect(getData, [formValues]);

  const updateRow = (row: TData, index: number) => {
    const temp = [...data];
    temp[index] = row;
    setData(temp);
  };

  useEffect(() => {
    if (urlData && codeTaking) {
      showLoading();
      api
        .$post<TExamMarketGenericResponse<{ statusId: number }>>(
          "/exam/confirmExamTakingDetailId",
          null,
          {
            params: {
              primaryProductId: urlData.primaryProductId,
              examTakingDetailId: codeTaking,
            },
          },
        )
        .then((res) => {
          const index = data.indexOf(
            data.find(
              (i) => i.primaryProductId == urlData.primaryProductId,
            ) as TData,
          );
          if (res.resultCode > 0) {
            updateRow?.({ ...urlData, statusId: res.statusId }, index);
          } else {
            console.error(res);
            Toast.error({ message: "خطایی رخ داده", position: "bottom" });
          }
        })
        .finally(hideLoading);
    }
  }, []);

  return (
    <div className={clsx("container", styles["management"])}>
      <Breadcrumb items={breadcrumbItems} />
      <h1>مدیریت اگزم مارکت</h1>
      <p>
        مشاهده وضعیت تمام آزمون های اگزم مارکت و تائید نهایی هر مرحله از آزمون
      </p>
      <SearchForm
        onSubmit={(values) => {
          setFormValues(values);
        }}
      />
      <Card className={styles["management__table-wrapper"]}>
        <div className={styles["management__table-wrapper__header"]}>
          <p>لیست مراحل آزمون های سال جاری</p>
          <p>
            <Info /> برای اجرای هر مرحله از آزمون باید کد آزمون را وارد کرده و
            تائید نهایی را انجام دهید
          </p>
        </div>
        <Table
          pagination
          style={{ width: "100%" }}
          onChangeData={async (alldata, row, index) => {
            if (row.examRequestId) {
              showLoading();
              api
                .$post<
                  TExamMarketGenericResponse<{
                    statusId: number;
                    questionNo: number;
                  }>
                >("/exam/setExamRequestByProvider", null, { params: row })
                .then((res) => {
                  if (res.statusId > 0) {
                    updateRow(
                      {
                        ...row,
                        statusId: res.statusId,
                        questionNo: res.questionNo,
                      },
                      index,
                    );
                  }
                })
                .catch((e) => {
                  console.error(e);
                  Toast.error({ message: "خطایی رخ داده", position: "bottom" });
                })
                .finally(hideLoading);
            }
          }}
          columns={getColumns(
            showLoading,
            hideLoading,
            examMarketModal,
            confirmationModal,
            updateRow,
          )}
          data={data}
          loading={isLoading}
        />
      </Card>
      <ExamMarketModal ref={examMarketModal} />
      <ConfirmationModal ref={confirmationModal} />
    </div>
  );
};

export default ExamMarketManagement;
